import { type StaticDecode, Type } from 'typebox'
import { Value } from 'typebox/value'

import { ReadyRosieVideo, StoryWithPages, type Story } from '@tadpoles/common'

export const LibraryCategories = {
    ebook: 'ebook',
    video: 'video'
} as const

export type LibraryCategory = StaticDecode<typeof LibraryCategory>
export const LibraryCategory = Type.Union([
    Type.Literal(LibraryCategories.ebook),
    Type.Literal(LibraryCategories.video)
])

export type LibraryItem = Story | ReadyRosieVideo

export type LibraryCatalogMetadata = StaticDecode<typeof LibraryCatalogMetadata>
export const LibraryCatalogMetadata = Type.Object(
    {
        refreshTimestamp: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER })
    },
    { additionalProperties: false }
)

export type LibraryCatalogBacking = {
    loadMetadata(category: LibraryCategory, signal: AbortSignal): Promise<unknown | null>
    loadStories(signal: AbortSignal): Promise<unknown | null>
    loadVideos(signal: AbortSignal): Promise<unknown | null>
    persistStories(
        stories: StoryWithPages[],
        metadata: LibraryCatalogMetadata,
        signal: AbortSignal
    ): Promise<void>
    persistVideos(
        videos: ReadyRosieVideo[],
        metadata: LibraryCatalogMetadata,
        signal: AbortSignal
    ): Promise<void>
}

export function decodeLibraryCatalogMetadata(value: unknown): LibraryCatalogMetadata {
    return Value.Decode(LibraryCatalogMetadata, value)
}

export function decodePersistedStories(value: unknown): StoryWithPages[] {
    return Value.Decode(Type.Array(StoryWithPages), value)
}

export function decodePersistedVideos(value: unknown): ReadyRosieVideo[] {
    const videos = Value.Decode(Type.Array(ReadyRosieVideo), value)
    const identifiers = new Set<string>()

    for (const video of videos) {
        if (identifiers.has(video.id)) {
            throw new Error('Persisted Library videos contain duplicate identifiers.')
        }

        identifiers.add(video.id)
    }

    return videos
}
